import { lazy, useEffect } from 'react';

import { QueryCache, QueryClient } from '@tanstack/react-query';

import { HTTPError } from '@enterprise/api';
import { isNumber, MINUTE } from '@enterprise/core';
import { useBoolean } from '@enterprise/hooks';

import { getEnvVar } from '@/utilities/get-env-var';

import type { DevtoolsButtonPosition } from './types/dev-tools-button-position.type';
import type { DevToolsPosition } from './types/dev-tools-position.type';

import { useCreateRouter } from './create-router.hook';

const rawMaxRetryAttempts = getEnvVar('VITE_APP_MAX_RETRY_ATTEMPTS');
const parsedRetryAttempts = isNumber(rawMaxRetryAttempts) ? Number.parseInt(rawMaxRetryAttempts, 10) : Number.NaN;
const maxRetryAttempts = Number.isFinite(parsedRetryAttempts) && parsedRetryAttempts >= 0 ? parsedRetryAttempts : 3;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: MINUTE * 3,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof HTTPError && error.response.status === 404) {
          return false;
        }

        return failureCount < maxRetryAttempts;
      },
      staleTime: MINUTE,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      console.groupCollapsed('QueryClient');
      console.error(error);
      console.groupEnd();
    },
  }),
});

const ReactQueryDevtoolsProduction = lazy(() => {
  return import('@tanstack/react-query-devtools/build/modern/production.js').then((module) => {
    return {
      default: module.ReactQueryDevtools,
    };
  });
});

export const useRouterConfiguration = () => {
  const [showDevtools, { toggle }] = useBoolean(false);

  const router = useCreateRouter(queryClient);

  useEffect(() => {
    // @ts-expect-error Defining this function for production builds, to be able to turn on the react query dev tools
    window.toggleDevtools = () => {
      toggle();
    };
  }, [toggle]);

  return {
    devToolButtonPosition: (getEnvVar('VITE_APP_TANSTACK_QUERY_BUTTON_POSITION') ??
      'bottom-left') as DevtoolsButtonPosition,
    devToolPosition: (getEnvVar('VITE_APP_TANSTACK_QUERY_POSITION') ?? 'bottom') as DevToolsPosition,
    queryClient,
    ReactQueryDevtoolsProduction,
    router,
    showDevtools,
  };
};
